import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  Divider,
} from "@mui/material";
import { School as SchoolIcon } from "@mui/icons-material";

const API_URL = process.env.REACT_APP_BASE_URL;

const TimeTable = () => {
  const [timetableData, setTimetableData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const token = sessionStorage.getItem("token");
    const classId = sessionStorage.getItem("classId");
    const sectionId = sessionStorage.getItem("sectionId");

    if (!token || !classId || !sectionId) {
      setError("Missing authentication or class details.");
      setLoading(false);
      return;
    }

    const fetchData = async () => {
      try {
        const response = await axios.post(
          `${API_URL}/TimeTable/GetTimeTable`,
          { classId, sectionId },
          { headers: { Authorization: token, "Content-Type": "application/json" } }
        );

        setTimetableData(processTimeTableData(response.data));
      } catch (err) {
        setError(err.response?.data?.message || "Failed to fetch timetable data");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const processTimeTableData = (data) => {
    if (!data || !Array.isArray(data)) return [];

    const periods = {};
    data.forEach((item) => {
      const key = `${item.startTime}-${item.endTime}-${item.periodSequenceNo}`;
      if (!periods[key]) {
        periods[key] = {
          startTime: item.startTime,
          endTime: item.endTime,
          period: `Period ${item.periodSequenceNo}`,
          periodSequenceNo: item.periodSequenceNo,
          monday: "-",
          tuesday: "-",
          wednesday: "-",
          thursday: "-",
          friday: "-",
          saturday: "-",
          sunday: "-",
        };
      }

      const dayMap = { 1: "sunday", 2: "monday", 3: "tuesday", 4: "wednesday", 5: "thursday", 6: "friday", 7: "saturday" };
      if (dayMap[item.dayofWeekId]) {
        let content = `${item.subjectName} ~ ${item.teacherName}`;
        if (item.roomNo && item.roomNo !== 0) content += `\nRoom ${item.roomNo}`;
        periods[key][dayMap[item.dayofWeekId]] = content;
      }
    });

    return Object.values(periods).sort((a, b) => a.periodSequenceNo - b.periodSequenceNo);
  };

  const formatTime = (timeStr) => {
    if (!timeStr) return "";
    if (timeStr.includes("AM") || timeStr.includes("PM")) return timeStr;

    const [hours, minutes] = timeStr.split(":").map(Number);
    const period = hours >= 12 ? "PM" : "AM";
    const displayHours = hours > 12 ? hours - 12 : hours || 12;
    return `${displayHours}:${minutes.toString().padStart(2, "0")} ${period}`;
  };

  return (
    <Box sx={{ p: 3, minHeight: "100vh" }}>
      <Card sx={{ maxWidth: "100%", mx: "auto", boxShadow: 3, borderRadius: 2 }}>
        <CardContent>
          <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
            <SchoolIcon sx={{ fontSize: 40, color: " rgba(75,108,183,1)", mr: 1 }} />
            <Typography variant="h4" sx={{ fontWeight: "bold", color: " rgba(49, 70, 119, 1)" }}>
              Time Table
            </Typography>
          </Box>

          <Divider sx={{ my: 2 }} />

          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
              <CircularProgress size={40} />
            </Box>
          ) : error ? (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          ) : (
            <TableContainer component={Paper} sx={{ boxShadow: 0, border: "1px solid #e0e0e0" }}>
              <Table sx={{ minWidth: 650 }} aria-label="timetable">
                <TableHead>
                  <TableRow sx={{ background: "linear-gradient( 110deg,  rgba(75,108,183,1) 11.3%, rgba(24,40,72,1) 99.3% )" }}>
                    <TableCell sx={{ color: "white", fontWeight: 600, p: 2 }}>Start Time</TableCell>
                    <TableCell sx={{ color: "white", fontWeight: 600, p: 2 }}>End Time</TableCell>
                    <TableCell sx={{ color: "white", fontWeight: 600, p: 2 }}>Period</TableCell>
                    <TableCell sx={{ color: "white", fontWeight: 600, p: 2 }}>Monday</TableCell>
                    <TableCell sx={{ color: "white", fontWeight: 600, p: 2 }}>Tuesday</TableCell>
                    <TableCell sx={{ color: "white", fontWeight: 600, p: 2 }}>Wednesday</TableCell>
                    <TableCell sx={{ color: "white", fontWeight: 600, p: 2 }}>Thursday</TableCell>
                    <TableCell sx={{ color: "white", fontWeight: 600, p: 2 }}>Friday</TableCell>
                    <TableCell sx={{ color: "white", fontWeight: 600, p: 2 }}>Saturday</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {timetableData.length > 0 ? (
                    timetableData.map((row, index) => (
                      <TableRow
                        key={index}
                        sx={{
                          "&:nth-of-type(odd)": { bgcolor: "#f9f9f9" },
                          "&:hover": { bgcolor: "#f0f0f0" },
                        }}
                      >
                        <TableCell sx={{ p: 2, textAlign: "center" }}>{formatTime(row.startTime)}</TableCell>
                        <TableCell sx={{ p: 2, textAlign: "center" }}>{formatTime(row.endTime)}</TableCell>
                        <TableCell sx={{ p: 2, textAlign: "center" }}>{row.period}</TableCell>
                        <TableCell sx={{ p: 2, whiteSpace: "pre-line" }}>{row.monday || "-"}</TableCell>
                        <TableCell sx={{ p: 2, whiteSpace: "pre-line" }}>{row.tuesday || "-"}</TableCell>
                        <TableCell sx={{ p: 2, whiteSpace: "pre-line" }}>{row.wednesday || "-"}</TableCell>
                        <TableCell sx={{ p: 2, whiteSpace: "pre-line" }}>{row.thursday || "-"}</TableCell>
                        <TableCell sx={{ p: 2, whiteSpace: "pre-line" }}>{row.friday || "-"}</TableCell>
                        <TableCell sx={{ p: 2, whiteSpace: "pre-line" }}>{row.saturday || "-"}</TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={9} sx={{ p: 3, textAlign: "center", color: "text.secondary" }}>
                        No timetable data available.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default TimeTable;
